import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Briefcase, Users, CalendarClock, CheckSquare, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { RoleBadge, StatusBadge } from "@/components/badges";
import { formatDate, meetingTypeLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const BOARD_ROLES = ["Styreleder", "Styremedlem", "Nestleder", "Varamedlem"];

const META = {
  selskaper: { title: "Dine selskaper", desc: "Alle selskaper du har tilgang til.", icon: Briefcase },
  styreverv: { title: "Dine styreverv", desc: "Selskaper der du sitter i styret.", icon: Users },
  moter: { title: "Kommende møter", desc: "Planlagte møter på tvers av selskapene dine.", icon: CalendarClock },
  oppgaver: { title: "Åpne oppgaver", desc: "Oppgaver som venter på oppfølging.", icon: CheckSquare },
};

function Row({ testid, onClick, icon: Icon, title, subtitle, right }) {
  return (
    <button data-testid={testid} onClick={onClick} className="group flex w-full items-center gap-4 rounded-xl border border-border/60 bg-card p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md">
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" strokeWidth={1.7} /></div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-headings text-base font-bold tracking-tight group-hover:text-primary">{title}</p>
        {subtitle ? <p className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</p> : null}
      </div>
      {right}
      <ArrowRight className="h-4 w-4 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
    </button>
  );
}

export default function DashboardList() {
  const { view } = useParams();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState(null);
  const [dash, setDash] = useState(null);
  const [showAllTasks, setShowAllTasks] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
    api.get("/dashboard").then((r) => setDash(r.data)).catch(() => setDash({}));
  }, []);

  const isTasks = view === "oppgaver";
  const meta = isTasks
    ? {
        title: showAllTasks ? "Alle åpne oppgaver" : "Mine åpne oppgaver",
        desc: showAllTasks ? "Alle oppgaver som venter på oppfølging i styrene dine." : "Oppgaver du er ansvarlig for.",
        icon: CheckSquare,
      }
    : META[view] || { title: "Oversikt", desc: "", icon: Building2 };
  const loading = companies === null || dash === null;

  let items = [];
  if (!loading) {
    if (view === "selskaper") {
      items = (companies || []).map((c) => (
        <Row key={c.id} testid={`list-company-${c.id}`} icon={Building2} onClick={() => navigate(`/selskap/${c.id}`)}
          title={c.name} subtitle={`${c.org_form || "AS"} · Org.nr ${c.org_number}${c.city ? ` · ${c.city}` : ""}`}
          right={<RoleBadge role={c.my_role} />} />
      ));
    } else if (view === "styreverv") {
      items = (companies || []).filter((c) => BOARD_ROLES.includes(c.my_role)).map((c) => (
        <Row key={c.id} testid={`list-board-${c.id}`} icon={Users} onClick={() => navigate(`/selskap/${c.id}`)}
          title={c.name} subtitle={`Org.nr ${c.org_number}`} right={<RoleBadge role={c.my_role} />} />
      ));
    } else if (view === "moter") {
      items = (dash?.upcoming_meetings || []).map((m) => (
        <Row key={m.id} testid={`list-meeting-${m.id}`} icon={CalendarClock} onClick={() => navigate(`/selskap/${m.company_id}/moter/${m.id}`)}
          title={m.title} subtitle={`${m.company_name} · ${formatDate(m.date)} · ${meetingTypeLabel(m.type)}`}
          right={<StatusBadge status={m.status} />} />
      ));
    } else if (view === "oppgaver") {
      items = (showAllTasks ? (dash?.open_tasks || []) : (dash?.my_open_tasks || [])).map((t) => (
        <Row key={t.id} testid={`list-task-${t.id}`} icon={CheckSquare} onClick={() => navigate(`/selskap/${t.company_id}/oppgaver`)}
          title={t.title} subtitle={`${t.company_name}${t.assignee_name ? ` · ${t.assignee_name}` : ""}${t.due_date ? ` · frist ${formatDate(t.due_date)}` : ""}`}
          right={<StatusBadge status={t.status} />} />
      ));
    }
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="sticky top-0 z-40 flex h-16 items-center border-b border-border/60 bg-background/80 px-5 backdrop-blur-xl sm:px-8">
        <Logo subtitle="Styrerom" />
        <div className="ml-auto"><ThemeToggle /></div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10 sm:px-8 lg:py-14">
        <Button variant="ghost" size="sm" className="mb-6 gap-2 text-muted-foreground" data-testid="back-to-dashboard" onClick={() => navigate("/hjem")}>
          <ArrowLeft className="h-4 w-4" /> Til dashbord
        </Button>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary"><meta.icon className="h-6 w-6" strokeWidth={1.7} /></div>
          <div>
            <h1 className="font-headings text-2xl font-extrabold tracking-tight sm:text-3xl">{meta.title}</h1>
            <p className="text-sm text-muted-foreground">{meta.desc}</p>
          </div>
          {isTasks ? (
            <Button variant="outline" size="sm" className="ml-auto" data-testid="toggle-all-tasks" onClick={() => setShowAllTasks((s) => !s)}>
              {showAllTasks ? "Vis mine oppgaver" : "Vis alle oppgaver"}
            </Button>
          ) : null}
        </motion.div>

        <div className="mt-8 space-y-3" data-testid="overview-list">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
            : items.length > 0
              ? items
              : <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
                  {isTasks && !showAllTasks ? "Ingen oppgaver er tildelt deg. Trykk «Vis alle oppgaver» for å se alle oppgaver i styrene dine." : "Ingen elementer å vise her ennå."}
                </div>}
        </div>
      </main>
    </div>
  );
}
